import EVENTS from "core/customEvent/events.js";
//import external from "../externalModules.js";
import anyHandlesOutsideImage from "./anyHandlesOutsideImage.js";
import { removeToolState } from "component/toolManager/toolState.js";
import triggerEvent from "core/customEvent/triggerEvent.js";
import { state } from "component/molecules/viewport/toolStates/store.js";
import math from "core/math";
import { getEnabledElement } from "component/toolManager/enabledElements.js";
import requestAnimationFrame from "core/render/requestAnimationFrame.js";

const runAnimation = {
  value: false
};

const _dragEvents = {
  mouse: [EVENTS.MOUSE_DRAG],
  touch: [EVENTS.TOUCH_DRAG]
};

const _upOrEndEvents = {
  mouse: [EVENTS.MOUSE_UP, EVENTS.MOUSE_CLICK],
  touch: [
    EVENTS.TOUCH_END,
    EVENTS.TOUCH_DRAG_END,
    EVENTS.TOUCH_PINCH,
    EVENTS.TOUCH_PRESS,
    EVENTS.TAP
  ]
};

/**
 * Move the provided handle
 *
 * @public
 * @method moveHandle
 * @memberof Manipulators
 *
 * @param {*} evtDetail
 * @param {*} toolName
 * @param {*} annotation
 * @param {*} handle
 * @param {*} [options={}]
 * @param {Boolean}  [options.deleteIfHandleOutsideImage]
 * @param {function} [options.doneMovingCallback]
 * @param {Boolean}  [options.preventHandleOutsideImage]
 * @param {*} [interactionType=mouse]
 * @returns {undefined}
 */
export default function(
  evtDetail,
  toolName,
  annotation,
  handle,
  options = {},
  interactionType = "mouse"
) {
  // 시작 핸들과 끝 핸들 중 어떤 핸들이 조작 되고 있는지 확인
  let activeHandle = "";

  if (annotation.handles.start === handle) {
    activeHandle = "start";
  } else if (annotation.handles.end === handle) {
    activeHandle = "end";
  } else if (annotation.handles.lb && annotation.handles.lb === handle) {
    activeHandle = "lb";
  } else if (annotation.handles.rt && annotation.handles.rt === handle) {
    activeHandle = "rt";
  }

  // Use global defaults, unless overidden by provided options
  options = Object.assign(
    {
      deleteIfHandleOutsideImage: state.deleteIfHandleOutsideImage,
      preventHandleOutsideImage: state.preventHandleOutsideImage,
      activeHandle: activeHandle
    },
    options
  );

  const element = evtDetail.element;
  const dragHandler = _dragHandler.bind(
    this,
    toolName,
    annotation,
    handle,
    options,
    interactionType
  );
  // So we don't need to inline the entire `upOrEndHandler` function
  const upOrEndHandler = evt => {
    _upOrEndHandler(
      toolName,
      evtDetail,
      annotation,
      handle,
      options,
      interactionType,
      {
        dragHandler,
        upOrEndHandler
      },
      evt
    );
  };

  handle.active = true;
  annotation.active = true;
  state.isToolLocked = true;

  // Add Event Listeners
  _dragEvents[interactionType].forEach(eventType => {
    //element.addEventListener(eventType, dragHandler);
    element.element.addEventListener(eventType, dragHandler);
  });
  _upOrEndEvents[interactionType].forEach(eventType => {
    //element.addEventListener(eventType, upOrEndHandler);
    element.element.addEventListener(eventType, upOrEndHandler);
  });

  // ==========================
  // ========  TOUCH ==========
  // ==========================
  if (interactionType === "touch") {
    runAnimation.value = true;
    //const enabledElement = getEnabledElement(element);
    const enabledElement = getEnabledElement(element.element);

    // Average pixel width of index finger is 45-57 pixels
    // https://www.smashingmagazine.com/2012/02/finger-friendly-design-ideal-mobile-touchscreen-target-sizes/
    const fingerDistance = -57;

    const aboveFinger = {
      x: evtDetail.currentPoints.page.x,
      y: evtDetail.currentPoints.page.y + fingerDistance
    };

    //const targetLocation = pageToPixel(element, aboveFinger.x, aboveFinger.y);
    const targetLocation = math.coordinate.pageToPixel(
      element.element,
      aboveFinger.x,
      aboveFinger.y
    );

    _animate(handle, runAnimation, enabledElement, targetLocation);
  }
}

function _dragHandler(
  toolName,
  annotation,
  handle,
  options,
  interactionType,
  evt
) {
  const { image, currentPoints, element } = evt.detail;
  const page = currentPoints.page;
  const fingerOffset = -57;
  const targetLocation = math.coordinate.pageToPixel(
    //element,
    element.element,
    page.x,
    interactionType === "touch" ? page.y + fingerOffset : page.y
  );

  runAnimation.value = false;
  handle.active = true;
  handle.hasMoved = true;
  handle.x = targetLocation.x;
  handle.y = targetLocation.y;

  // TODO: 특정 핸들의 움직임으로 위치에 영향받는 핸들을 조절한다.
  if (
    options.activeHandle === "start" &&
    (annotation.handles.lb && annotation.handles.rt)
  ) {
    annotation.handles.lb.x = targetLocation.x;
    annotation.handles.rt.y = targetLocation.y;
  } else if (
    options.activeHandle === "end" &&
    (annotation.handles.lb && annotation.handles.rt)
  ) {
    annotation.handles.lb.y = targetLocation.y;
    annotation.handles.rt.x = targetLocation.x;
  } else if (
    options.activeHandle === "lb" &&
    (annotation.handles.lb && annotation.handles.rt)
  ) {
    annotation.handles.start.x = targetLocation.x;
    annotation.handles.end.y = targetLocation.y;
  } else if (
    options.activeHandle === "rt" &&
    (annotation.handles.lb && annotation.handles.rt)
  ) {
    annotation.handles.start.y = targetLocation.y;
    annotation.handles.end.x = targetLocation.x;
  }
  // TODO: A way to not flip this for textboxes on annotations
  annotation.invalidated = true;

  if (annotation.storeMiddlePoint && annotation.storeMiddlePoint === true) {
    if (options.activeHandle === "start") {
      annotation.middlePoint.unshift({
        x: targetLocation.x,
        y: targetLocation.y
      });
      annotation.handles.start.x = targetLocation.x;
      annotation.handles.start.y = targetLocation.y;
    } else if (options.activeHandle === "end") {
      annotation.middlePoint.push({
        x: targetLocation.x,
        y: targetLocation.y
      });
    }
  }

  if (options.preventHandleOutsideImage) {
    math.range.clipToBox(handle, image);
  }

  // update
  let enabledElement = getEnabledElement(element.element);
  enabledElement.needsRedraw = true;

  const eventType = EVENTS.MEASUREMENT_MODIFIED;
  const modifiedEventData = {
    toolName,
    element,
    measurementData: annotation
  };

  triggerEvent(element.element, eventType, modifiedEventData);
}

/**
 * @description Measurement 도구 그리기 상태 종료시 호출되는 핸들러
 * @function _upOrEndHandler
 * @param {*} toolName
 * @param {*} evtDetail
 * @param {*} annotation
 * @param {*} handle
 * @param {*} [options={}]
 * @param {*} interactionType
 * @param {*} { dragHandler, upOrEndHandler }
 * @param {*} evt
 */
function _upOrEndHandler(
  toolName,
  evtDetail,
  annotation,
  handle,
  options = {},
  interactionType,
  { dragHandler, upOrEndHandler },
  evt
) {
  const image = evtDetail.currentPoints.image;
  const element = evt.detail.element;

  handle.active = false;
  annotation.active = false;
  // TODO: A way to not flip this for textboxes on annotations
  annotation.invalidated = true;
  state.isToolLocked = false;
  runAnimation.value = false;

  // Remove Event Listeners
  _dragEvents[interactionType].forEach(eventType => {
    //element.removeEventListener(eventType, dragHandler);
    element.element.removeEventListener(eventType, dragHandler);
  });
  _upOrEndEvents[interactionType].forEach(eventType => {
    //element.removeEventListener(eventType, upOrEndHandler);
    element.element.removeEventListener(eventType, upOrEndHandler);
  });

  // If any handle is outside the image, delete the tool data
  if (
    options.deleteIfHandleOutsideImage &&
    anyHandlesOutsideImage(evtDetail, annotation.handles)
  ) {
    //removeToolState(element, toolName, annotation);
    removeToolState(element.element, toolName, annotation);
  }

  // TODO: What dark magic makes us want to handle TOUCH_PRESS differently?
  if (evt.type === EVENTS.TOUCH_PRESS) {
    evt.detail.handlePressed = annotation;
    handle.x = image.x; // Original Event
    handle.y = image.y;
  }

  if (typeof options.doneMovingCallback === "function") {
    options.doneMovingCallback();
  }
  // update
  let enabledElement = getEnabledElement(element.element);
  enabledElement.needsRedraw = true;
}

/**
 * Animates the provided handle using `requestAnimationFrame`
 * @private
 * @method _animate
 *
 * @param {*} handle
 * @param {*} runAnimation
 * @param {*} enabledElement
 * @param {*} targetLocation
 * @returns {undefined}
 */
function _animate(handle, runAnimation, enabledElement, targetLocation) {
  if (!runAnimation.value) {
    return;
  }

  // Pixels / second
  const distanceRemaining = Math.abs(handle.y - targetLocation.y);
  const linearDistEachFrame = distanceRemaining / 10;

  if (distanceRemaining < 1) {
    handle.y = targetLocation.y;
    runAnimation.value = false;

    return;
  }

  if (handle.y > targetLocation.y) {
    handle.y -= linearDistEachFrame;
  } else if (handle.y < targetLocation.y) {
    handle.y += linearDistEachFrame;
  }

  // Request a new frame
  requestAnimationFrame(function() {
    _animate(handle, runAnimation, enabledElement, targetLocation);
  });
}
